import React, { useCallback, useEffect, useRef, useState } from 'react';
import FlowHandlingButtons from '../Common/HandleNavigationAndSubmit';
import { contentIsNotNull } from '../Common/Validator';
import { spaceListingData } from '../Common/ProfileListingDataModal';
import { IMAGE_CLOUD_FOLDER, IMAGE_CLOUD_NAME, IMAGE_CLOUD_PRESET } from '../../../settings';
import { getProfileData, setProfileData } from '../Common/DataController';
import { DEFAULT_DATA_OBJ } from '../SpaceListingConstants';
import { Col, Row, Icon, Spin } from 'antd';
import { useParams } from 'react-router-dom';
import ImageEnhanceCompareModal from './ImageEnhanceCompareModal';
import ImagePreviewModal from './ImagePreviewModal';
import {
  enhanceImageFile,
  fetchImageAsFile,
  uploadBlobToCloudinary,
} from '../../../utils/imageEnhanceUtils';

const ENHANCE_MODAL_IDLE = 'idle';
const ENHANCE_MODAL_LOADING = 'loading';
const ENHANCE_MODAL_COMPARE = 'compare';
const ENHANCE_MODAL_UPLOADING = 'uploading';

const UploadImages = ({ currentBlock }) => {
  const { country } = useParams();
  const [uploadImageDetails, setUploadImageDetails] = useState(getProfileData()?.[DEFAULT_DATA_OBJ] ? getProfileData() : spaceListingData(country));
  const [isNextBtnActive, setIsNextBtnActive] = useState(false);
  const [enhancingAssetId, setEnhancingAssetId] = useState(null);
  const [enhanceModal, setEnhanceModal] = useState({
    visible: false,
    phase: ENHANCE_MODAL_IDLE,
    error: null,
    assetId: null,
    originalUrl: null,
    enhancedPreviewUrl: null,
  });
  const enhancedPreviewUrlRef = useRef(null);
  const enhancedBlobRef = useRef(null);
  const [previewImage, setPreviewImage] = useState(null);

  const revokeEnhancedPreview = useCallback(() => {
    if (enhancedPreviewUrlRef.current) {
      URL.revokeObjectURL(enhancedPreviewUrlRef.current);
      enhancedPreviewUrlRef.current = null;
    }
    enhancedBlobRef.current = null;
  }, []);

  useEffect(() => {
    return () => revokeEnhancedPreview();
  }, [revokeEnhancedPreview]);

  useEffect(() => {
    const isImages = getUploadedImages().filter((key) => key?.resource_type !== 'video');
    setIsNextBtnActive(contentIsNotNull(isImages, 0, 10));
  }, [uploadImageDetails]);

  const getUploadedImages = () => {
    return uploadImageDetails?.[currentBlock]?.[0]?.uploadedImages || [];
  };

  const persistUploadState = (mutator) => {
    const profile = getProfileData() || uploadImageDetails || spaceListingData(country);
    const blocks = profile[currentBlock] || [{ selectedImage: {}, uploadedImages: [] }];

    const updatedBlocks = blocks.map((block) => {
      const next = {
        ...block,
        uploadedImages: Array.isArray(block.uploadedImages) ? [...block.uploadedImages] : [],
      };
      mutator(next);
      return next;
    });

    const tempObj = {
      ...profile,
      [currentBlock]: updatedBlocks,
    };

    setProfileData(tempObj);
    setUploadImageDetails(tempObj);
  };

  const addUploadedAsset = (info) => {
    if (!info || !info.asset_id) return;
    const existing = getProfileData()?.[currentBlock]?.[0]?.uploadedImages || [];
    if (existing.some((img) => img.asset_id === info.asset_id)) return;
    persistUploadState((block) => {
      block.uploadedImages = [...block.uploadedImages, info];
      block.selectedImage = info.asset_id;
    });
  };

  const addUploadedAssetRef = useRef(addUploadedAsset);
  addUploadedAssetRef.current = addUploadedAsset;

  const changeSelectedImage = (newValue) => {
    if (newValue) {
      persistUploadState((block) => {
        block.selectedImage = newValue;
      });
    }
  };

  const removeSelectedImage = (id) => {
    persistUploadState((block) => {
      block.uploadedImages = block.uploadedImages.filter((img) => img?.asset_id !== id);
      if (block.selectedImage === id) {
        block.selectedImage = block.uploadedImages.find((img) => img?.resource_type === 'image')?.asset_id;
      }
    });
  };

  const removeSelectedVideo = (id) => {
    persistUploadState((block) => {
      block.uploadedImages = block.uploadedImages.filter((img) => img?.asset_id !== id);
    });
  };

  const replaceUploadedImage = (oldAssetId, newCloudinaryInfo) => {
    persistUploadState((block) => {
      block.uploadedImages = block.uploadedImages.map((img) =>
        img?.asset_id === oldAssetId
          ? { ...newCloudinaryInfo, aiEnhanced: true }
          : img
      );
      if (block.selectedImage === oldAssetId) {
        block.selectedImage = newCloudinaryInfo.asset_id;
      }
    });
  };

  const getFieldCurrentValue = (type) => {
    return uploadImageDetails?.[currentBlock]?.[0]?.[type];
  };

  const isCloudinaryUploadComplete = (result) => {
    if (!result || !result.info) return false;
    return result.event === 'success' || result.event === 'queues-end';
  };

  const closeEnhanceModal = useCallback(() => {
    revokeEnhancedPreview();
    setEnhancingAssetId(null);
    setEnhanceModal({
      visible: false,
      phase: ENHANCE_MODAL_IDLE,
      error: null,
      assetId: null,
      originalUrl: null,
      enhancedPreviewUrl: null,
    });
  }, [revokeEnhancedPreview]);

  const startEnhance = async (imageInfo, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (enhancingAssetId) return;

    const assetId = imageInfo?.asset_id;
    const imageUrl = imageInfo?.url;
    if (!assetId || !imageUrl) return;

    revokeEnhancedPreview();
    setEnhancingAssetId(assetId);
    setEnhanceModal({
      visible: true,
      phase: ENHANCE_MODAL_LOADING,
      error: null,
      assetId,
      originalUrl: imageUrl,
      enhancedPreviewUrl: null,
    });

    try {
      const file = await fetchImageAsFile(imageUrl, `space-${assetId}.jpg`);
      const enhancedBlob = await enhanceImageFile(file);
      enhancedBlobRef.current = enhancedBlob;
      const previewUrl = URL.createObjectURL(enhancedBlob);
      enhancedPreviewUrlRef.current = previewUrl;

      setEnhancingAssetId(null);
      setEnhanceModal((prev) => ({
        ...prev,
        phase: ENHANCE_MODAL_COMPARE,
        enhancedPreviewUrl: previewUrl,
        error: null,
      }));
    } catch (err) {
      setEnhancingAssetId(null);
      setEnhanceModal((prev) => ({
        ...prev,
        phase: ENHANCE_MODAL_IDLE,
        error: err.message || 'Something went wrong. Please try again.',
      }));
    }
  };

  const handleKeepOriginal = () => {
    closeEnhanceModal();
  };

  const handleUseEnhanced = async () => {
    const { assetId, enhancedPreviewUrl } = enhanceModal;
    if (!assetId || !enhancedPreviewUrl) return;

    setEnhanceModal((prev) => ({ ...prev, phase: ENHANCE_MODAL_UPLOADING, error: null }));

    try {
      const blob = enhancedBlobRef.current;
      if (!blob) {
        throw new Error('Enhanced image is no longer available. Please try again.');
      }
      const cloudinaryInfo = await uploadBlobToCloudinary(blob, `enhanced-${assetId}.png`);
      replaceUploadedImage(assetId, cloudinaryInfo);
      closeEnhanceModal();
    } catch (err) {
      setEnhanceModal((prev) => ({
        ...prev,
        phase: ENHANCE_MODAL_COMPARE,
        error: err.message || 'Failed to save enhanced image. Please try again.',
      }));
    }
  };

  const openWidget = () => {
    window.cloudinary.openUploadWidget(
      {
        cloud_name: IMAGE_CLOUD_NAME,
        upload_preset: IMAGE_CLOUD_PRESET,
        theme: 'white',
        folder: IMAGE_CLOUD_FOLDER,
        client_allowed_formats: ['png', 'jpeg', 'pdf'],
        cropping: 'true',
        croppingCoordinatesMode: 'custom',
      },
      (error, result) => {
        if (!error && isCloudinaryUploadComplete(result)) {
          addUploadedAssetRef.current(result.info);
        }
      }
    );
  };

  const openVideoWidget = () => {
    window.cloudinary.openUploadWidget(
      {
        cloud_name: IMAGE_CLOUD_NAME,
        upload_preset: IMAGE_CLOUD_PRESET,
        theme: 'white',
        folder: IMAGE_CLOUD_FOLDER,
        client_allowed_formats: ['mp4'],
        cropping: 'true',
        croppingCoordinatesMode: 'custom',
      },
      (error, result) => {
        if (!error && isCloudinaryUploadComplete(result)) {
          persistUploadState((block) => {
            block.uploadedImages = [...block.uploadedImages, result.info];
          });
        }
      }
    );
  };

  const selectImage = (asset_id) => {
    changeSelectedImage(asset_id);
  };

  const openImagePreview = (imageInfo, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (!imageInfo?.url) return;
    setPreviewImage({
      url: imageInfo.url,
      title: imageInfo.aiEnhanced ? 'AI-enhanced image' : 'Uploaded image',
    });
  };

  const closeImagePreview = () => {
    setPreviewImage(null);
  };

  const imageList = getUploadedImages().filter((value) => {
    if (['mp4', '3gp'].includes(value?.format)) return false;
    return value?.resource_type == 'image' || (value?.url && value?.resource_type !== 'video');
  });

  return (
    <>
      <div className="left-side-inner-wrapper upload-img-page">
        <h1 className="listing-page-title">Uploads</h1>
        <p className="listing-page-description">
          Upload your best images. The human brain processes visuals up to 60,000 times faster than text: the better the images, the more bookings you’ll get!
        </p>

        <div className="sl-upload-container">
          <Row gutter={32}>
            {/* Images Column */}
            <Col md={12} sm={24}>
              <div className="sl-upload-section">
                <h4 className="sl-upload-label">Images</h4>
                <div className="sl-dropzone" onClick={() => openWidget(true)}>
                  <div className="sl-dropzone-inner">
                    <div className="sl-upload-icon-circle">
                      <Icon type="cloud-upload" />
                    </div>
                    <p className="sl-dropzone-text">
                      <span className="sl-upload-link">Upload</span>
                    </p>
                    <p className="sl-dropzone-hint">Supported formats: JPEG, PNG</p>
                  </div>
                </div>
                <p className="sl-resolution-text">Minimum image resolution is 800 x 410px</p>

                <div className="sl-preview-grid">
                  <Row gutter={[8, 8]}>
                    {imageList.map((value) => {
                      const isEnhancing = enhancingAssetId === value?.asset_id;
                      const isPrimary = value?.asset_id === getFieldCurrentValue('selectedImage');
                      return (
                        <Col span={8} key={value?.asset_id}>
                          <div className="sl-preview-item">
                            <div className={`sl-preview-card ${isPrimary ? 'is-primary' : ''}`}>
                              <div
                                className="sl-preview-media"
                                onClick={(e) => openImagePreview(value, e)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    openImagePreview(value, e);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                aria-label="View image"
                              >
                                <img src={value.url} alt="preview" />
                              {value?.aiEnhanced && <span className="sl-ai-badge">AI</span>}
                              {isEnhancing && (
                                <div className="sl-preview-enhancing">
                                  <Spin size="small" />
                                </div>
                              )}
                              </div>
                              <div className="sl-preview-overlay">
                                <Icon
                                  type="eye"
                                  className="sl-preview-view-hint"
                                  onClick={(e) => openImagePreview(value, e)}
                                />
                                <Icon
                                  type="delete"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeSelectedImage(value?.asset_id);
                                  }}
                                />
                                {isPrimary ? (
                                  <Icon type="check-circle" theme="filled" />
                                ) : (
                                  <Icon
                                    type="star"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      selectImage(value?.asset_id);
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                            {!value?.aiEnhanced && (
                              <button
                                type="button"
                                className="sl-ai-enhance-btn"
                                disabled={!!enhancingAssetId || enhanceModal.visible}
                                onClick={(e) => startEnhance(value, e)}
                              >
                                <Icon type="rocket" />
                                Enhance with AI
                              </button>
                            )}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              </div>
            </Col>

            {/* Videos Column */}
            <Col md={12} sm={24}>
              <div className="sl-upload-section">
                <h4 className="sl-upload-label">Videos</h4>
                <div className="sl-dropzone" onClick={() => openVideoWidget(true)}>
                  <div className="sl-dropzone-inner">
                    <div className="sl-upload-icon-circle">
                      <Icon type="cloud-upload" />
                    </div>
                    <p className="sl-dropzone-text">
                      <span className="sl-upload-link">Upload</span>
                    </p>
                    <p className="sl-dropzone-hint">Supported formats: MP4, 3GP</p>
                  </div>
                </div>
                <p className="sl-resolution-text">
                  Maximum video resolution is 800 x 410px and duration is 1 minute and the capacity is 100 MB.
                </p>

                <div className="sl-preview-grid">
                  <Row gutter={[8, 8]}>
                    {getFieldCurrentValue('uploadedImages') &&
                      getFieldCurrentValue('uploadedImages')
                        .filter((value) => ['mp4', '3gp']?.includes(value?.format))
                        .map((value) => (
                          <Col span={8} key={value?.asset_id}>
                            <div className="sl-preview-card is-video">
                              <video src={value.url} />
                              <div className="sl-preview-overlay">
                                <Icon type="delete" onClick={(e) => { e.stopPropagation(); removeSelectedVideo(value?.asset_id); }} />
                              </div>
                            </div>
                          </Col>
                        ))}
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <ImagePreviewModal
        visible={!!previewImage}
        imageUrl={previewImage?.url}
        title={previewImage?.title}
        onClose={closeImagePreview}
      />

      <ImageEnhanceCompareModal
        visible={enhanceModal.visible}
        phase={enhanceModal.phase}
        error={enhanceModal.error}
        originalUrl={enhanceModal.originalUrl}
        enhancedPreviewUrl={enhanceModal.enhancedPreviewUrl}
        onKeepOriginal={handleKeepOriginal}
        onUseEnhanced={handleUseEnhanced}
        onClose={closeEnhanceModal}
      />

      <FlowHandlingButtons isActive={isNextBtnActive} />
    </>
  );
};

export default UploadImages;
